import { useAuth } from '@agentnet/auth';
import { createStyles, FormControlLabel, Grid, makeStyles, Switch, Theme, Typography } from '@agentnet/components';
import DateFnsUtils from '@date-io/date-fns';
import {
  AgentNetButton,
  CellLink,
  CellMessage,
  CellStatus,
  Content,
  DashboardMetricCard,
  DataTable,
  PageHeader,
  pxToRem,
} from '@fluentsms/agentnet-web-components';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import {
  CellClickedEvent,
  ColDef,
  ColumnState,
  FilterChangedEvent,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  MenuItemDef,
  SelectionChangedEvent,
  SortChangedEvent,
} from 'ag-grid-enterprise';
import {
  getLockboxNumberApi,
  getOrderFileDetailsApi,
  getRemittanceReceivables,
  mergePdfs,
} from 'api/orderManagement/order-management-api';
import clsx from 'clsx';
import { format as formatDate, parse } from 'date-fns';
import { dateFilters } from 'features/orderManagement/Dashboard';
import {
  getMemberNames,
  RemittanceReceivablesCategory,
  RemittanceReceivablesColor,
  RemittanceReceivablesStatus,
} from 'features/orderManagement/enum';
import { OrderNewContainerPrefillData } from 'features/orderManagement/OrderCreateContainer';
import {
  LockboxNumberType,
  MergePdfsRequestModel,
  MergePdfsResponseModel,
  OrderFileDetailsType,
  OrderType,
  RemittanceReceivableResponseModel,
  RemittanceReceivableSearchRequestModel,
} from 'features/orderManagement/types';
import { OrderCreateContext } from 'hooks/OrderCreateContext';
import useAsync from 'hooks/useAsync';
import useSessionStorage from 'hooks/useSessionStorage';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import { CalendarIcon } from 'ui-kit/icons/OrderManagement';
import PDFIcon from 'ui-kit/icons/PDF';
import { AgentNetTextInput } from 'ui-kit/inputs';
import AgentNetDropdownSelector from 'ui-kit/inputs/AgentNetDropdownSelector';
import DateFieldString from 'ui-kit/inputs/DateField/DateFieldString';
import { getDateRangeFromPeriod, getFullName } from 'utilities/utilities';
import { RemittanceReceivableOrderDrawer } from './RemittanceReceivableOrderDrawer';
import { RemittanceReceivablesModifyAssignmentContainer } from './RemittanceReceivablesModifyAssignmentContainer';

const dataQaPrefix = 'OrderManagement-RemittanceReceivables';

export function RemittanceReceivablesContent() {
  const [displayedRowCount, setDisplayedRowCount] = useState<number>(0);
  const [selectedRows, setSelectedRows] = useState<RemittanceReceivableResponseModel[]>([]);
  const [modifyAssignmentDrawerOpen, setEditAssignmentDrawerOpen] = useState<boolean>(false);
  const [editAssignmentData, setEditAssignmentData] = useState<RemittanceReceivableResponseModel[]>([]);
  const [useWindowForPdf, setUseWindowForPdf] = useState<boolean>(false);
  const { getAccessToken } = useAuth();
  const gridApiRef = useRef<GridApi<RemittanceReceivableResponseModel> | null>(null);
  const userColumnStateRef = useRef<ColumnState[] | null>(null);
  const defaultColumnStateRef = useRef<ColumnState[] | null>(null);
  const isInitialMountRef = useRef<boolean>(true);
  const { setOrderPrefillData } = useContext(OrderCreateContext) ?? {};
  const history = useHistory();
  const [sessionFilterModel, setSessionFilterModel] = useSessionStorage('remittanceReceivablesFilterModel');
  const [sessionSortModel, setSessionSortModel] = useSessionStorage('remittanceReceivablesSortModel');
  const [sessionGroupModel, setSessionGroupModel] = useSessionStorage('remittanceReceivablesGroupModel');
  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);

  // Advanced search session storage
  const [advancedSearch, setAdvancedSearch] = useSessionStorage('remittanceReceivablesAdvancedSearch');
  const [sessionSearchExecuted, setSessionSearchExecuted] = useSessionStorage('remittanceReceivablesSearchExecuted');
  const [sessionDateFilter, setSessionDateFilter] = useSessionStorage('remittanceReceivablesDateFilter');
  const [sessionStartDate, setSessionStartDate] = useSessionStorage('remittanceReceivablesStartDate');
  const [sessionEndDate, setSessionEndDate] = useSessionStorage('remittanceReceivablesEndDate');
  const [sessionLockboxId, setSessionLockboxId] = useSessionStorage('remittanceReceivablesLockboxId');
  const [sessionCheckNumber, setSessionCheckNumber] = useSessionStorage('remittanceReceivablesCheckNumber');
  const [sessionCategoryCdId, setSessionCategoryCdId] = useSessionStorage('remittanceReceivablesCategoryCdId');
  const [sessionEntityName, setSessionEntityName] = useSessionStorage('remittanceReceivablesEntityName');
  const [sessionStatusCdId, setSessionStatusCdId] = useSessionStorage('remittanceReceivablesStatusCdId');

  // Advanced search form state
  const [dateFilter, setDateFilter] = useState<string>(sessionDateFilter ?? '');
  const [lockboxId, setLockboxId] = useState<number | undefined>(sessionLockboxId ?? undefined);
  const [checkNumber, setCheckNumber] = useState<string>(sessionCheckNumber || '');
  const [categoryCdId, setCategoryCdId] = useState<number | undefined>(sessionCategoryCdId ?? undefined);
  const [entityName, setEntityName] = useState<string>(sessionEntityName || '');
  const [statusCdId, setStatusCdId] = useState<number | undefined>(sessionStatusCdId ?? undefined);
  const [searchExecuted, setSearchExecuted] = useState<boolean>(false);
  const [lockboxOptions, setLockboxOptions] = useState<LockboxNumberType[]>([]);

  if (!sessionSortModel) {
    // default sort state is itemColumn ascending
    setSessionSortModel([{ colId: 'itemColumn', sort: 'asc' }]);
  }

  // Default advanced search to true on initial load
  useEffect(() => {
    if (advancedSearch === null) {
      setAdvancedSearch(true);
    }
  }, []);

  const getDefaultStartDate = (): Date => {
    const today = new Date();
    let daysToSubtract = 1;

    // Monday, go back to Friday
    if (today.getDay() === 1) {
      daysToSubtract = 3;
    }
    // Sunday, go back to Friday
    else if (today.getDay() === 0) {
      daysToSubtract = 2;
    }

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - daysToSubtract);
    return yesterday;
  };

  const [startDate, setStartDate] = useState<Date>(
    sessionStartDate ? new Date(sessionStartDate) : getDefaultStartDate(),
  );
  const [endDate, setEndDate] = useState<Date>(sessionEndDate ? new Date(sessionEndDate) : new Date());

  // Update date range based on selected date filter preset
  useEffect(() => {
    if (dateFilter === 'custom' || dateFilter === '') {
      // Don't auto-update dates for custom or blank selection
      return;
    }
    const { from, to } = getDateRangeFromPeriod(dateFilter);
    setStartDate(from);
    setEndDate(to);
  }, [dateFilter]);

  // Load lockbox options for dropdown
  useEffect(() => {
    const fetchLockboxes = async () => {
      try {
        const token = await getAccessToken();
        const lockboxes = await getLockboxNumberApi(token);
        setLockboxOptions([{ name: '', value: 0 }, ...lockboxes]);
      } catch (error) {
        console.error('Failed to load lockbox options:', error);
      }
    };
    fetchLockboxes();
  }, [getAccessToken]);

  // Category dropdown options - values from RemittanceReceivablesCategory enum
  const categoryOptions = [
    { name: '', value: '' },
    { name: 'Payment Only', value: RemittanceReceivablesCategory['Payment Only'] },
    { name: 'Scan Only', value: RemittanceReceivablesCategory['Scan Only'] },
    { name: 'Order', value: RemittanceReceivablesCategory.Order },
    { name: 'Refund', value: RemittanceReceivablesCategory.Refund },
    { name: 'Misc. Correspondence', value: RemittanceReceivablesCategory['Misc. Correspondence'] },
    { name: 'Missing Backup', value: RemittanceReceivablesCategory['Missing Backup'] },
    { name: 'Non-FATICO', value: RemittanceReceivablesCategory['Non-FATICO'] },
    { name: 'Missing Agent ID', value: RemittanceReceivablesCategory['Missing Agent ID'] },
    { name: 'Rescan', value: RemittanceReceivablesCategory['Rescan'] },
    { name: 'Inventory Requested', value: RemittanceReceivablesCategory['Inventory Requested'] },
    { name: 'PFSC Clarification Required', value: RemittanceReceivablesCategory['PFSC Clarification Required'] },
    { name: 'Cash Clarification Required', value: RemittanceReceivablesCategory['Cash Clarification Required'] },
    { name: 'Non-Agency Premium', value: RemittanceReceivablesCategory['Non-Agency Premium'] },
    { name: 'Prepayment', value: RemittanceReceivablesCategory['Prepayment'] },
  ];

  // Status dropdown options
  const statusOptions = [
    { name: '', value: '' },
    { name: 'Unassigned', value: RemittanceReceivablesStatus.Unassigned },
    { name: 'Assigned', value: RemittanceReceivablesStatus.Assigned },
    { name: 'Closed', value: RemittanceReceivablesStatus.Closed },
  ];

  const headerCheckboxRenderer = () => {
    return <input type="checkbox" data-testid={'OrderManagement-RemittanceReceivables-HeaderCheckbox'} />;
  };

  const rowCheckboxRenderer = () => {
    return <input type="checkbox" data-testid={'OrderManagement-RemittanceReceivables-RowCheckbox'} />;
  };

  const columnDateRenderer = (params: { value: Date }) => {
    if (!params.value) {
      return <div></div>;
    }
    return <div>{params.value ? formatDate(params.value, 'MM/dd/yyyy') : ''}</div>;
  };

  const getDateFilterParams = () => ({
    comparator: (filterLocalDateAtMidnight: Date, cellDate: Date) => {
      cellDate.setHours(0, 0, 0, 0);
      if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
        return 0;
      }
      if (cellDate.getTime() < filterLocalDateAtMidnight.getTime()) {
        return -1;
      }
      if (cellDate.getTime() > filterLocalDateAtMidnight.getTime()) {
        return 1;
      }
      return 0;
    },
    browserDatePicker: true,
    dateFormat: 'yyyy-mm-dd',
    inRangeInclusive: true,
  });

  const mergePdfsUi = (pdfUrls: string[], usePopup: boolean) => {
    executeMergePdfs({ pdfUrls: pdfUrls });
    setUseWindowForPdf(usePopup);
  };

  const getColorsByStatus = (): { [status: string]: string } => {
    const colors: { [status: string]: string } = {};
    const statusColors = getMemberNames(RemittanceReceivablesColor);
    let statusColor = statusColors.next();
    while (!statusColor.done) {
      const colorCode = RemittanceReceivablesColor[statusColor.value as keyof typeof RemittanceReceivablesColor];
      colors[statusColor.value] = '#' + colorCode.toString(16).padStart(6, '0');
      statusColor = statusColors.next();
    }
    return colors;
  };

  const colorsByStatus = getColorsByStatus();

  const formatUSD = (totalSelectedAmount: any): string => {
    return totalSelectedAmount?.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const DownloadPdfRenderer = (params: ICellRendererParams<RemittanceReceivableResponseModel>) => {
    return (
      <a aria-label="Download PDF" onClick={() => mergePdfsUi([params.data?.s3FilePdf || ''], false)}>
        <PDFIcon />
      </a>
    );
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        colId: 'checkboxColumn',
        field: '',
        maxWidth: 40,
        filter: false,
        floatingFilter: false,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        showDisabledCheckboxes: true,
        pinned: 'left',
        headerComponent: headerCheckboxRenderer,
        cellRenderer: rowCheckboxRenderer,
        resizable: false,
        suppressMovable: true,
        suppressColumnsToolPanel: true,
      },
      {
        colId: 'checkNumberColumn',
        field: 'checkNumber',
        headerName: 'Check Number',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        minWidth: 125,
        maxWidth: 256,
        enableRowGroup: true,
      },
      {
        colId: 'batchColumn',
        field: 'batchNumber',
        headerName: 'Batch',
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        minWidth: 144,
        enableRowGroup: true,
      },
      {
        colId: 'itemColumn',
        field: 'itemNumber',
        headerName: 'Item',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        enableRowGroup: true,
        minWidth: 144,
        maxWidth: 256,
      },
      {
        colId: 'categoryColumn',
        field: 'remitReceivableCategoryName',
        headerName: 'Category',
        filter: 'agSetColumnFilter',
        suppressFloatingFilterButton: false,
        enableRowGroup: true,
        minWidth: 144,
        maxWidth: 256,
      },
      {
        colId: 'statusColumn',
        field: 'remitReceivableStatusName',
        headerName: 'Status',
        cellRenderer: (params: ICellRendererParams) => {
          return <CellStatus status={params.value} colorsByStatus={colorsByStatus} />;
        },
        filter: 'agSetColumnFilter',
        enableRowGroup: true,
        suppressFloatingFilterButton: false,
        minWidth: 144,
        maxWidth: 256,
      },
      {
        colId: 'orderIdColumn',
        field: 'orderId',
        headerName: 'Order ID',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        enableRowGroup: true,
        minWidth: 144,
        maxWidth: 256,
        cellRenderer: CellLink,
        cellRendererParams: () => ({
          linkTemplate: 'order-management',
        }),
        onCellClicked: (event: CellClickedEvent<RemittanceReceivableResponseModel>) => {
          if (!event.data || !event.data.orderId) {
            return;
          }
          getOrderFileDetails(event.data.orderId).then((orderFileDetails) => {
            const row = event.data!;
            const myOrder: OrderType = {
              firmId: orderFileDetails.firmId,
              lockboxId: row.remitBatchSourceId,
              orderId: row.orderId!,
              orderDate: row.processedDate,
              referenceNumber: row.batchNumber.toString(),
              checkCount: orderFileDetails.checkCount,
              reportedAmount: orderFileDetails.reportedAmount,
              targetTab: 'orderQueue',
              firm: {
                firmId: orderFileDetails.firmId,
                firmName: orderFileDetails.firmName,
                isActive: true,
              },
              lockboxNumber: {
                name: row.remitReceivableBatchSourceName,
                value: row.remitBatchSourceId!,
              },
            };
            history.push('/order-management', { order: myOrder, from: '/order-management/remittance-receivables' });
          });
        },
      },
      {
        colId: 'entityNameColumn',
        field: 'entityName',
        headerName: 'Entity Name',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        enableRowGroup: true,
        minWidth: 144,
        maxWidth: 256,
      },
      {
        colId: 'lockboxNameColumn',
        field: 'remitReceivableBatchSourceName',
        headerName: 'Lockbox Name',
        filter: 'agSetColumnFilter',
        suppressFloatingFilterButton: false,
        enableRowGroup: true,
        minWidth: 144,
        maxWidth: 256,
      },
      {
        colId: 'processedDateColumn',
        field: 'processedDateJs',
        type: 'dateColumn',
        cellDataType: 'date',
        headerName: 'Processed Date',
        filterParams: getDateFilterParams(),
        cellRenderer: columnDateRenderer,
        filter: false,
        sort: 'desc',
        enableRowGroup: true,
        minWidth: 188,
        maxWidth: 188,
      },
      {
        colId: 'assigneeColumn',
        field: 'assigneeFullName',
        headerName: 'Assignee',
        filter: 'agTextColumnFilter',
        suppressFloatingFilterButton: false,
        enableRowGroup: true,
        minWidth: 144,
        maxWidth: 300,
      },
      {
        colId: 'amountColumn',
        field: 'amount',
        headerName: 'Item Amount',
        filter: 'agNumberColumnFilter',
        enableRowGroup: true,
        suppressFloatingFilterButton: false,
        cellRenderer: (params: ICellRendererParams<RemittanceReceivableResponseModel>) => {
          return <div style={{ textAlign: 'left', width: '100%', paddingLeft: '15px' }}>{formatUSD(params.value)}</div>;
        },
        pinned: 'right',
        minWidth: 124,
        maxWidth: 124,
      },
      {
        colId: 'modifiedByColumn',
        field: 'modifiedByFullName',
        headerName: 'Modified By',
        filter: 'agTextColumnFilter',
        suppressFloatingFilterButton: false,
        enableRowGroup: true,
        minWidth: 144,
        maxWidth: 300,
      },
      {
        colId: 'modifiedDateColumn',
        field: 'modifiedDateJs',
        type: 'dateColumn',
        cellDataType: 'date',
        headerName: 'Modified Date',
        filterParams: getDateFilterParams(),
        cellRenderer: columnDateRenderer,
        filter: false,
        enableRowGroup: true,
        minWidth: 188,
        maxWidth: 188,
      },
      {
        colId: 'pageCountColumn',
        field: 'pageCount',
        headerName: 'Page Count',
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        enableRowGroup: true,
        minWidth: 110,
      },
      {
        colId: 'entityRoutingNumberColumn',
        field: 'entityRoutingNumber',
        headerName: 'Entity Routing Number',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        enableRowGroup: true,
        minWidth: 130,
      },
      {
        colId: 'entityAccountNumberColumn',
        field: 'entityAccountNumber',
        headerName: 'Entity Account Number',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        enableRowGroup: true,
        minWidth: 150,
      },
      {
        colId: 'downloadPdfColumn',
        field: '',
        floatingFilter: false,
        sortable: false,
        cellRenderer: DownloadPdfRenderer,
        maxWidth: 20,
        cellStyle: { display: 'flex', justifyContent: 'center', cursor: 'pointer', alignItems: 'center' },
        tooltipValueGetter: () => 'Download PDF',
        pinned: 'right',
        suppressColumnsToolPanel: true,
        lockVisible: true,
      },
      {
        field: '',
        floatingFilter: false,
        cellRenderer: CellMessage,
        onCellClicked: (e: CellClickedEvent) => {
          setSelectedConversationId(e.data.remitReceivableId);
        },
        width: 60,
        minWidth: 60,
        tooltipValueGetter: () => 'Open conversation panel',
        pinned: 'right',
        suppressHeaderFilterButton: true,
        suppressColumnsToolPanel: true,
        lockVisible: true,
      },
    ],
    [],
  );

  const [filteredRows, setFilteredRows] = useState<RemittanceReceivableResponseModel[]>([]);
  const [rowData, setRowData] = useState<RemittanceReceivableResponseModel[]>([]);

  const mergePdfsApi = async (request: MergePdfsRequestModel): Promise<MergePdfsResponseModel> => {
    return mergePdfs(request, await getAccessToken());
  };

  const {
    execute: executeMergePdfs,
    status: mergePdfsStatus,
    value: mergePdfsValue,
  } = useAsync<MergePdfsResponseModel>(mergePdfsApi, false);

  const getRemittanceReceivablesApi = async (
    searchRequest?: RemittanceReceivableSearchRequestModel,
  ): Promise<RemittanceReceivableResponseModel[]> => {
    const request: RemittanceReceivableSearchRequestModel = searchRequest || {
      startDate: dateFilter === '' ? undefined : formatDate(startDate, 'yyyy-MM-dd'),
      endDate: dateFilter === '' ? undefined : formatDate(endDate, 'yyyy-MM-dd'),
      lockboxId,
      checkNumber: checkNumber || undefined,
      categoryCdId,
      entityName: entityName || undefined,
      statusCdId,
    };
    return getRemittanceReceivables(await getAccessToken(), request);
  };

  const {
    execute: executeGetRemittanceReceivables,
    status: getRemittanceReceivablesStatus,
    value: getRemittanceReceivablesValue,
  } = useAsync<RemittanceReceivableResponseModel[], [RemittanceReceivableSearchRequestModel?]>(
    getRemittanceReceivablesApi,
    false,
  );

  const getOrderFileDetails = async (orderId: number): Promise<OrderFileDetailsType> => {
    return getOrderFileDetailsApi(orderId, await getAccessToken());
  };

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        width: '100%',
        marginTop: pxToRem(114),
      },
      selectedRows: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100% !important',
        fontSize: pxToRem(14),
        padding: theme.spacing(3),
        paddingBottom: 12,
      },
      heading: {
        marginLeft: 0,
        marginRight: 0,
        marginBottom: 0,
      },
      contentWrap: {
        justifyContent: 'space-between',
        display: 'flex',
        flexDirection: 'column',
      },
      contentClass: {
        margin: '0 auto auto',
      },
      tableDefault: {
        height: `calc(100vh - ${selectedRows.length ? '550px' : rowData?.length ? '500px' : '600px'}) !important`,
        fontFamily: theme.typography.fontFamily,
        overflow: 'visible !important',
        '& .ag-root-wrapper': {
          overflow: 'visible !important',
        },
        '& .ag-popup': {
          overflow: 'visible !important',
        },
        '& .ag-column-select-list': {
          maxHeight: '500px !important',
          overflowY: 'auto !important',
        },
      },
      paddingX: {
        paddingLeft: theme.spacing(0),
        paddingRight: theme.spacing(0),
        width: 'auto',
      },
      noRows: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 220,
        justifyContent: 'center',
      },
      personBackground: {
        backgroundColor: '#696d7033',
        borderRadius: 50,
      },
      dashboardMetricCard: {
        width: '20%',
      },
    }),
  );
  const classes = useStyles();

  const totalSelectedAmount = selectedRows.reduce((left, right) => {
    return left + (right.amount ?? 0);
  }, 0);

  // Execute search if user previously searched (restore search state on return to dashboard)
  useEffect(() => {
    if (sessionSearchExecuted) {
      setSearchExecuted(true);
      executeGetRemittanceReceivables();
    }
  }, []);

  useEffect(() => {
    if (getRemittanceReceivablesStatus === 'success' && getRemittanceReceivablesValue) {
      for (const row of getRemittanceReceivablesValue) {
        row.assigneeFullName = getFullName({
          firstName: row.assigneeFirstName,
          middleName: row.assigneeMiddleName,
          lastName: row.assigneeLastName,
        });
        row.modifiedByFullName =
          row.modifiedByUserId === 0 || !row.modifiedByUserId
            ? 'System'
            : getFullName({
                firstName: row.modifiedByFirstName,
                middleName: row.modifiedByMiddleName,
                lastName: row.modifiedByLastName,
              });

        if (row.processedDate) {
          row.processedDateJs = new Date(row.processedDate);
        }
        if (row.modifiedDate) {
          row.modifiedDateJs = new Date(row.modifiedDate);
        }
      }
      setRowData(getRemittanceReceivablesValue);
      setFilteredRows(getRemittanceReceivablesValue);
    }
  }, [getRemittanceReceivablesStatus, getRemittanceReceivablesValue]);

  useEffect(() => {
    if (mergePdfsStatus === 'success' && mergePdfsValue) {
      const pdfUrl = mergePdfsValue.mergedPdfUrl;
      if (useWindowForPdf) {
        window.open(pdfUrl, 'newwindow', 'width=1024,height=768');
      } else {
        window.open(pdfUrl, '_blank');
      }
    }
  }, [mergePdfsStatus, mergePdfsValue]);

  const renderBottomAppBar = () => {
    return (
      selectedRows.length > 0 && (
        <BottomAppBar
          primaryActionLabel="Modify Assignment"
          primaryActionDataQa={'Remittance-Receivables-Modify-Assignment'}
          disablePrimary={
            !selectedRows.every(
              (selectedRow) =>
                selectedRow.remitReceivableBatchSourceName == selectedRows[0].remitReceivableBatchSourceName,
            )
          }
          secondaryActionLabel="Preview Documents"
          disableSecondary={!selectedRows.length}
          onSecondaryClick={() => {
            const s3Pdfs = selectedRows.map((row) => row.s3FilePdf);
            mergePdfsUi(s3Pdfs, true);
          }}
          cancelLabel="Clear All"
          cancelDataQa={'Remittance-Receivables-Clear-All'}
          onPrimaryClick={() => {
            setEditAssignmentDrawerOpen(true);
            setEditAssignmentData([...selectedRows]);
          }}
          omitPrimaryBorder={true}
          onCancel={() => gridApiRef?.current?.deselectAll()}
        />
      )
    );
  };
  const assignedCount = filteredRows.filter(
    (item) => item.remitReceivableStatusCdId === RemittanceReceivablesStatus.Assigned,
  ).length;
  const unassignedCount = filteredRows.filter(
    (item) => item.remitReceivableStatusCdId === RemittanceReceivablesStatus.Unassigned,
  ).length;
  const closedCount = filteredRows.filter(
    (item) => item.remitReceivableStatusCdId === RemittanceReceivablesStatus.Closed,
  ).length;
  const batchesCount = new Set(filteredRows.map((item) => item.batchNumber)).size;
  const amountSum = filteredRows.reduce((sum, item) => sum + (item.amount || 0), 0);

  const today = new Date();
  const maxStartDate = endDate < today ? endDate : today;

  const onEditAssignmentSaved = useCallback(() => {
    gridApiRef.current?.deselectAll();
    gridApiRef.current?.onFilterChanged();
  }, []);

  // Save column state whenever user modifies columns
  const onAnyColumnChanged = useCallback(() => {
    if (gridApiRef.current) {
      userColumnStateRef.current = gridApiRef.current.getColumnState();
    }
  }, []);

  // Custom reset to default column state
  const resetToDefaults = useCallback(() => {
    if (gridApiRef.current && defaultColumnStateRef.current) {
      userColumnStateRef.current = null; // Clear user state to prevent re-applying after reset
      gridApiRef.current.applyColumnState({ state: defaultColumnStateRef.current, applyOrder: true });
    }
  }, []);

  // Override column header menu to use custom reset
  const getMainMenuItems = useCallback(
    (params: { defaultItems?: (string | MenuItemDef)[] }) => {
      const menuItems: (string | MenuItemDef)[] = params.defaultItems ? [...params.defaultItems] : [];
      // Find and replace the reset columns item
      const resetIndex = menuItems.findIndex((item) => item === 'resetColumns');
      if (resetIndex !== -1) {
        menuItems[resetIndex] = {
          name: 'Reset Columns',
          action: () => resetToDefaults(),
        } as MenuItemDef;
      }
      return menuItems;
    },
    [resetToDefaults],
  );

  const restoreFilterState = useCallback((api: GridApi) => {
    api.setFilterModel(sessionFilterModel);
    setDisplayedRowCount(-1);
  }, []);

  const restoreSortState = useCallback(
    (api: GridApi) => {
      if (sessionSortModel) {
        api.applyColumnState({ state: sessionSortModel, applyOrder: false });
      }
    },
    [sessionSortModel],
  );

  const restoreGroupState = useCallback(
    (api: GridApi) => {
      if (sessionGroupModel) {
        api.applyColumnState({ state: sessionGroupModel, applyOrder: false });
      }
    },
    [sessionGroupModel],
  );

  const handleSearch = () => {
    // Save search criteria to session storage
    setSessionSearchExecuted(true);
    setSessionDateFilter(dateFilter);
    setSessionStartDate(dateFilter === '' ? null : formatDate(startDate, 'yyyy-MM-dd'));
    setSessionEndDate(dateFilter === '' ? null : formatDate(endDate, 'yyyy-MM-dd'));
    setSessionLockboxId(lockboxId ?? null);
    setSessionCheckNumber(checkNumber || null);
    setSessionCategoryCdId(categoryCdId ?? null);
    setSessionEntityName(entityName || null);
    setSessionStatusCdId(statusCdId ?? null);

    setSearchExecuted(true);
    executeGetRemittanceReceivables();
  };

  const handleClearSearch = () => {
    // Reset form fields
    setDateFilter('today');
    setStartDate(new Date());
    setEndDate(new Date());
    setLockboxId(undefined);
    setCheckNumber('');
    setCategoryCdId(undefined);
    setEntityName('');
    setStatusCdId(undefined);

    // Clear session storage
    setSessionSearchExecuted(null);
    setSessionDateFilter(null);
    setSessionStartDate(null);
    setSessionEndDate(null);
    setSessionLockboxId(null);
    setSessionCheckNumber(null);
    setSessionCategoryCdId(null);
    setSessionEntityName(null);
    setSessionStatusCdId(null);

    setSearchExecuted(false);
    setRowData([]);
    setFilteredRows([]);
  };

  return (
    <ContentContainer fullWidth>
      <Content>
        <div>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <PageHeader
                className={classes.heading}
                divider={false}
                title={'Remittance Receivables'}
                contentRight={
                  <div style={{ display: 'flex' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          name="Advanced Search"
                          checked={advancedSearch ?? true}
                          onChange={() => {
                            setAdvancedSearch(!advancedSearch);
                          }}
                          data-testid={`${dataQaPrefix}-Advanced-Search-Toggle`}
                        />
                      }
                      labelPlacement="start"
                      label={'Advanced Search'}
                      data-testid={`${dataQaPrefix}-Advanced-Search-Toggle-Label`}
                    />
                  </div>
                }
              />
            </Grid>
          </Grid>
          {advancedSearch && (
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <AgentNetDropdownSelector
                  name="lockboxId"
                  data-testid={`${dataQaPrefix}-Advanced-Search-Lockbox`}
                  options={lockboxOptions}
                  value={lockboxId ?? ''}
                  id="lockboxId"
                  label="Lockbox"
                  dropdowntype="outlined"
                  menuOption={(value: number | string) => {
                    setLockboxId(value === '' || Number(value) <= 0 ? undefined : Number(value));
                    return false;
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <AgentNetDropdownSelector
                  name="dateFilter"
                  data-testid={`${dataQaPrefix}-Advanced-Search-Date-Filter`}
                  options={dateFilters ?? []}
                  value={dateFilter}
                  id="dateFilter"
                  label="Processing Date Range"
                  dropdowntype="outlined"
                  menuOption={(value: string) => {
                    setDateFilter(value);
                    return false;
                  }}
                  prefixIcon={<CalendarIcon />}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <DateFieldString
                    label="Processing Date - From"
                    format="MM/dd/yyyy"
                    value={dateFilter === '' ? '' : formatDate(startDate, 'MM/dd/yyyy')}
                    disabled={dateFilter !== 'custom'}
                    onChange={(date) => {
                      if (date) {
                        const parsedDate = parse(date, 'MM/dd/yyyy', new Date());
                        if (parsedDate <= endDate) {
                          setStartDate(parsedDate);
                        } else {
                          setStartDate(endDate);
                        }
                      }
                    }}
                    maxDate={formatDate(maxStartDate, 'MM/dd/yyyy')}
                  />
                </MuiPickersUtilsProvider>
              </Grid>
              <Grid item xs={6} sm={3}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <DateFieldString
                    label="Processing Date - To"
                    format="MM/dd/yyyy"
                    value={dateFilter === '' ? '' : formatDate(endDate, 'MM/dd/yyyy')}
                    disabled={dateFilter !== 'custom'}
                    onChange={(date) => {
                      if (date) {
                        const parsedDate = parse(date, 'MM/dd/yyyy', new Date());
                        if (parsedDate >= startDate) {
                          setEndDate(parsedDate);
                        } else {
                          setEndDate(startDate);
                        }
                      }
                    }}
                    minDate={formatDate(startDate, 'MM/dd/yyyy')}
                    maxDate={formatDate(new Date(), 'MM/dd/yyyy')}
                  />
                </MuiPickersUtilsProvider>
              </Grid>
              <Grid item xs={6} sm={3}>
                <AgentNetTextInput
                  fullWidth
                  data-testid={`${dataQaPrefix}-Advanced-Search-CheckNumber`}
                  label="Check Number"
                  value={checkNumber}
                  onChange={(event) => {
                    setCheckNumber(event.target.value?.trim() ?? '');
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <AgentNetDropdownSelector
                  name="categoryCdId"
                  data-testid={`${dataQaPrefix}-Advanced-Search-Category`}
                  options={categoryOptions}
                  value={categoryCdId ?? ''}
                  id="categoryCdId"
                  label="Category"
                  dropdowntype="outlined"
                  menuOption={(value: number | string) => {
                    setCategoryCdId(value === '' ? undefined : Number(value));
                    return false;
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <AgentNetTextInput
                  fullWidth
                  data-testid={`${dataQaPrefix}-Advanced-Search-EntityName`}
                  label="Entity Name"
                  value={entityName}
                  onChange={(event) => {
                    setEntityName(event.target.value ?? '');
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <AgentNetDropdownSelector
                  name="statusCdId"
                  data-testid={`${dataQaPrefix}-Advanced-Search-Status`}
                  options={statusOptions}
                  value={statusCdId ?? ''}
                  id="statusCdId"
                  label="Status"
                  dropdowntype="outlined"
                  menuOption={(value: number | string) => {
                    setStatusCdId(value === '' ? undefined : Number(value));
                    return false;
                  }}
                />
              </Grid>
              <Grid item xs={2} sm={1}>
                <AgentNetButton
                  color="primary"
                  data-testid={`${dataQaPrefix}-ClearSearch`}
                  onClick={handleClearSearch}
                  variant="outlined"
                  id="clear-search"
                  size="small"
                >
                  Clear Search
                </AgentNetButton>
              </Grid>
              <Grid item xs={2} sm={1}>
                <AgentNetButton
                  color="primary"
                  data-testid={`${dataQaPrefix}-Search`}
                  variant="contained"
                  onClick={handleSearch}
                  id="search"
                  size="small"
                >
                  Search
                </AgentNetButton>
              </Grid>
            </Grid>
          )}
          <div style={{ marginTop: 16 }} />
          {getRemittanceReceivablesStatus === 'pending' && (
            <div className={classes.noRows}>
              <LoadingSpinner status="pending" className="files-container--spinner-root" />
            </div>
          )}
          {searchExecuted && getRemittanceReceivablesStatus !== 'pending' && (
            <>
              <Grid container spacing={2}>
                <Grid item xs="auto" className={classes.dashboardMetricCard}>
                  <DashboardMetricCard heading="Assigned Items" status="Searching" value={assignedCount.toString()} />
                </Grid>
                <Grid item xs="auto" className={classes.dashboardMetricCard}>
                  <DashboardMetricCard
                    heading="Unassigned Items"
                    status="Quality Review"
                    value={unassignedCount.toString()}
                  />
                </Grid>
                <Grid item xs="auto" className={classes.dashboardMetricCard}>
                  <DashboardMetricCard heading="Closed Items" status="Examining" value={closedCount.toString()} />
                </Grid>
                <Grid item xs="auto" className={classes.dashboardMetricCard}>
                  <DashboardMetricCard heading="Batches" status="Searching" value={batchesCount.toString()} />
                </Grid>
                <Grid item xs="auto" className={classes.dashboardMetricCard}>
                  <DashboardMetricCard heading="Amount" status="Quality Review" value={formatUSD(amountSum)} />
                </Grid>
                <Grid item xs={12}>
                  <div className={clsx(classes.tableDefault, classes.paddingX)}>
                    <DataTable
                      defaultColDef={{
                        editable: false,
                        filter: 'agTextColumnFilter',
                        flex: 1,
                        floatingFilter: true,
                        resizable: true,
                        sortable: true,
                        suppressFloatingFilterButton: true,
                        suppressHeaderFilterButton: true,
                        suppressHeaderMenuButton: true,
                        suppressSizeToFit: false,
                        width: 144,
                        minWidth: 144,
                      }}
                      rowHeight={40}
                      rowGroupPanelShow="always"
                      animateRows={true}
                      noRowsOverlayComponent={'customOverlayNoRows'}
                      groupDefaultExpanded={-1}
                      gridOptions={{
                        suppressRowClickSelection: true,
                        rowSelection: 'multiple',
                        groupSelectsChildren: true,
                        // Add these properties:
                        groupDisplayType: 'groupRows',
                        showOpenedGroup: true,
                        getMainMenuItems: getMainMenuItems,
                      }}
                      groupRowRendererParams={{
                        suppressCount: true,
                        innerRenderer: (params: ICellRendererParams<RemittanceReceivableResponseModel>) => {
                          const children = params.node.allLeafChildren || [];
                          let sum = 0;

                          // BFS all leaf nodes to calculate the sum of amounts
                          children.forEach((child) => {
                            if (child.data && typeof child.data.amount === 'number') {
                              sum += child.data.amount;
                            }
                          });

                          const allSelected = children.length > 0 && children.every((child) => child.isSelected());

                          return (
                            <div style={{ display: 'block !important', alignItems: 'center', position: 'relative' }}>
                              <input
                                type="checkbox"
                                checked={allSelected}
                                style={{ marginRight: '8px' }}
                                onChange={(event) => {
                                  params.api.setNodesSelected({ nodes: children, newValue: event.target.checked });
                                }}
                                data-testid={`GroupCheckbox-${params.value}`}
                              />
                              <strong>{params.value}</strong>
                              <span
                                style={{
                                  position: 'fixed',
                                  width: '100px',
                                  right: '70px', // Position to align with the amount column
                                }}
                              >
                                <strong>Total: </strong>
                                {formatUSD(sum)}
                              </span>
                            </div>
                          );
                        },
                      }}
                      onGridReady={(event: GridReadyEvent) => {
                        gridApiRef.current = event.api;

                        // On initial mount, hide the 5 columns and capture as default state
                        if (isInitialMountRef.current) {
                          event.api.setColumnsVisible(
                            [
                              'modifiedByColumn',
                              'modifiedDateColumn',
                              'pageCountColumn',
                              'entityRoutingNumberColumn',
                              'entityAccountNumberColumn',
                            ],
                            false,
                          );
                          defaultColumnStateRef.current = event.api.getColumnState();
                          isInitialMountRef.current = false;
                        } else {
                          // On subsequent grid ready (shouldn't happen but be safe), restore user state
                          if (userColumnStateRef.current) {
                            event.api.applyColumnState({ state: userColumnStateRef.current, applyOrder: true });
                          }
                        }

                        restoreFilterState(event.api);
                        restoreSortState(event.api);
                        restoreGroupState(event.api);
                      }}
                      onColumnVisible={onAnyColumnChanged}
                      onColumnMoved={onAnyColumnChanged}
                      onColumnResized={onAnyColumnChanged}
                      onColumnPinned={onAnyColumnChanged}
                      onColumnRowGroupChanged={(event) => {
                        const groupState = event.api.getColumnState().filter((col) => col.rowGroup === true);
                        setSessionGroupModel(groupState);
                      }}
                      onFilterChanged={(event: FilterChangedEvent<RemittanceReceivableResponseModel>) => {
                        const filteredRows: RemittanceReceivableResponseModel[] = [];
                        event.api.forEachNodeAfterFilterAndSort((node) => {
                          if (node.data) {
                            filteredRows.push(node.data);
                          }
                        });
                        setFilteredRows(filteredRows);
                        const filterSelectedRows: RemittanceReceivableResponseModel[] = [];
                        event.api.getSelectedRows().forEach((selectedRow) => {
                          if (filteredRows.includes(selectedRow)) {
                            filterSelectedRows.push(selectedRow);
                          }
                        });
                        setSelectedRows(filterSelectedRows);
                        setSessionFilterModel(event.api.getFilterModel());
                        setDisplayedRowCount(event.api.getDisplayedRowCount());
                      }}
                      onSortChanged={(event: SortChangedEvent<RemittanceReceivableResponseModel>) => {
                        const sortState = event.api.getColumnState().filter((col) => col.sort != null);
                        setSessionSortModel(sortState);
                      }}
                      onSelectionChanged={(event: SelectionChangedEvent<RemittanceReceivableResponseModel>) => {
                        // Get selected rows in the order they appear on the grid
                        const selectedRowsInOrder: RemittanceReceivableResponseModel[] = [];

                        event.api.forEachNodeAfterFilterAndSort((node) => {
                          if (node.isSelected() && node.data) {
                            selectedRowsInOrder.push(node.data);
                          }
                        });

                        if (
                          selectedRowsInOrder.length &&
                          selectedRowsInOrder.every((row) => row.batchNumber === selectedRowsInOrder[0].batchNumber)
                        ) {
                          const firstRow = selectedRowsInOrder[0];
                          setOrderPrefillData?.({
                            orderDate: firstRow.processedDate,
                            remitReceivableBatchSourceName: firstRow.remitReceivableBatchSourceName,
                            remitBatchSourceId: firstRow.remitBatchSourceId,
                            batchNumber: firstRow.batchNumber.toString(),
                            numberOfChecks: selectedRowsInOrder.length,
                            reportedNet: selectedRowsInOrder.reduce((sum, row) => sum + (row.amount ?? 0), 0),
                            remitReceivableIds: selectedRowsInOrder.map((row) => row.remitReceivableId),
                          } as OrderNewContainerPrefillData);
                        } else {
                          setOrderPrefillData?.(null);
                        }
                        setSelectedRows(selectedRowsInOrder);
                      }}
                      columnDefs={columnDefs}
                      rowData={rowData}
                      maintainColumnOrder
                      fixedHeader
                      alwaysShowHorizontalScroll
                    />
                  </div>
                  {totalSelectedAmount > 0 && selectedRows.length > 0 && (
                    <Typography variant="body2">
                      Selecting {selectedRows.length} items for {formatUSD(totalSelectedAmount)}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    Showing {displayedRowCount < 0 ? rowData.length : displayedRowCount} of {rowData.length} items
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}
          {renderBottomAppBar()}
          {modifyAssignmentDrawerOpen && (
            <RemittanceReceivablesModifyAssignmentContainer
              open={modifyAssignmentDrawerOpen}
              setOpen={setEditAssignmentDrawerOpen}
              data={editAssignmentData}
              onSaved={onEditAssignmentSaved}
            />
          )}
          {selectedConversationId && (
            <RemittanceReceivableOrderDrawer
              remitReceivableId={selectedConversationId}
              onClose={() => setSelectedConversationId(null)}
            />
          )}
        </div>
      </Content>
    </ContentContainer>
  );
}
